<script setup lang="ts">
import { computed } from 'vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import type { ContractManagementViewColumnBulkAddResult, ExtractionPromptSearchEntry } from '@/api';
import PromptPicker from './PromptPicker.vue';

/** The prompt picker in a modal: closes once a prompt is picked or a contract type's are added. */
interface Props {
  show: boolean;
  /** Persisted mode: marks the prompts this view already asks (from the backend). */
  viewUid?: string;
  /** Preview mode: the prompts the wizard's columns already link. */
  usedPromptUids?: string[];
  /**
   * Persisted mode, when picking a new column: each contract type row can add all of its
   * prompts to the view as columns at once.
   */
  bulkAdd?: boolean;
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'select-prompt', prompt: ExtractionPromptSearchEntry): void;
  /** A contract type's prompts were added to the view as columns; the modal has closed. */
  (e: 'bulk-added', result: ContractManagementViewColumnBulkAddResult): void;
}

const props = withDefaults(defineProps<Props>(), {
  viewUid: undefined,
  usedPromptUids: () => [],
  bulkAdd: false,
});

const emit = defineEmits<Emits>();

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

function handleSelectPrompt(prompt: ExtractionPromptSearchEntry): void {
  emit('select-prompt', prompt);
  showModal.value = false;
}

function handleBulkAdded(result: ContractManagementViewColumnBulkAddResult): void {
  emit('bulk-added', result);
  showModal.value = false;
}
</script>

<template>
  <gaia-modal v-model:show="showModal" width="900px" title="Select a prompt">
    <!-- Mounted on each open, so the picker starts again from the contract types. -->
    <prompt-picker
      v-if="showModal"
      :view-uid="viewUid"
      :used-prompt-uids="usedPromptUids"
      :bulk-add="bulkAdd"
      :style="{ minHeight: '450px' }"
      @select-prompt="handleSelectPrompt"
      @bulk-added="handleBulkAdded"
    />

    <template #footer>
      <n-flex justify="end">
        <gaia-button @click="showModal = false">Close</gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
